// 서명 슬롯 표시(widget:'signature') — 그 행 인물의 서명 이미지, 없으면 빈 서명 칸.
//
// 작성폼 트리(반복 행 안 위젯)·행 그리드(RowsCollectionEditor)·formTable 반복 셀·본문 반복 셀
// (RenderTemplate)이 공유하는 **단일 표시 컴포넌트**다(surface별 재구현 금지 — AnnotatedImageView와
// 같은 규약). 저작 캔버스와 배포 작성 페이지가 같은 렌더러를 쓰므로, 이 한 벌이 곧 캔버스 WYSIWYG이다.
//
// P2: 값은 resolved 채널(서버 listRenderMap → liveData __slotSignatures)에서 오고, 수집 상호작용
// (칸 탭 → 서명 패드)은 **host 주입**이다 — rc는 도메인(모달·API)을 모른 채 "탭 가능한 칸 +
// onRequestSign/onRequestDelete 콜백"까지만 소유한다(photo 위젯 uploadPhoto 채널과 같은 결).
// 콜백 미주입 host(admin 캔버스·external 공유·생성 모드)는 표시 전용으로 degrade하되 모양은
// 실폼과 동일하다(WYSIWYG — 어포던스는 레이아웃 중립 absolute 오버레이만).
//
// 값을 content에 쓰지 않는 것은 설계다 — 증빙이 작성자가 자유 편집하는 content에 살면 무결성이
// 없다(설계 §3-2).
//
// 빈 칸을 "서명 없음" 같은 문구 대신 **밑줄 있는 빈 상자**로 그리는 이유: 이 칸은 사진처럼
// "있으면 좋은 표시물"이 아니라 **아직 받지 않은 서명 자리**다. 종이 양식의 서명란과 같은 모양이어야
// 저작자도 작성자도 그것이 채워질 자리임을 안다.
//
// rc 패키지 규약: 스타일은 전부 인라인 — 이 패키지에서만 처음 쓰는 Tailwind 유틸은 소비 앱의
// JIT가 생성하지 않아 조용히 무스타일이 된다(feedback-rc-package-tailwind-class-purge-inline-style).
//
// 설계: docs/decisions/2026-08-28-row-signature-slot-design.md

import type { CSSProperties, MouseEvent } from 'react';
import {
  resolveSignatureSlotKey,
  slotSignatureFor,
  type DataSourceFieldMap,
  type SlotSignatureRenderView,
} from '@safetyhub/shared';

/** 서명 칸 높이(px) — 이미지·빈 칸 공통. 사진(112)보다 낮다: 서명은 가로로 긴 획이다. */
const SIGNATURE_SLOT_HEIGHT = 56;

/** 빈 서명 칸 — 종이 양식 서명란 결(점선 테두리 + 옅은 안내 문구). */
const EMPTY_SLOT_STYLE: CSSProperties = {
  width: '100%',
  height: SIGNATURE_SLOT_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px dashed #d1d5db',
  borderRadius: 4,
  background: '#ffffff',
  color: '#9ca3af',
  fontSize: '0.75rem',
};

/** 서명 이미지 — 칸 안에서 비율 유지(잘라 채우지 않는다: 획이 잘리면 증빙이 훼손된다). */
const SIGNATURE_IMG_STYLE: CSSProperties = {
  display: 'block',
  maxWidth: '100%',
  maxHeight: SIGNATURE_SLOT_HEIGHT,
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
};

/** 서명 이미지를 담는 칸 — 빈 칸과 같은 높이라 서명 유무로 행 높이가 튀지 않는다. */
const SIGNED_SLOT_STYLE: CSSProperties = {
  width: '100%',
  height: SIGNATURE_SLOT_HEIGHT,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

/**
 * 탭 가능한 칸의 버튼 리셋 — 시각은 위 두 스타일이 소유하고, 버튼화는 동작만 더한다
 * (레이아웃 중립 — 기본 버튼 UA 스타일이 칸 크기·배경을 바꾸면 WYSIWYG가 깨진다).
 */
const TAPPABLE_RESET_STYLE: CSSProperties = {
  padding: 0,
  margin: 0,
  font: 'inherit',
  color: 'inherit',
  textAlign: 'inherit',
  cursor: 'pointer',
};

/** 어포던스 오버레이 기준점 — 흐름 폭·높이를 차지하지 않는 래퍼(block + relative만). */
const OVERLAY_ANCHOR_STYLE: CSSProperties = { position: 'relative', display: 'block' };

/** 서명 지우기(×) — 우상단 absolute 오버레이(레이아웃 중립, 작성 모드 한정). */
const DELETE_BADGE_STYLE: CSSProperties = {
  position: 'absolute',
  top: 2,
  right: 2,
  width: 18,
  height: 18,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #e5e7eb',
  borderRadius: 9,
  background: '#ffffff',
  color: '#6b7280',
  fontSize: 11,
  lineHeight: 1,
  cursor: 'pointer',
  padding: 0,
};

/** 슬롯 주소 — (slotKey, rowId?). rowId 없음 = 단독 배치 슬롯. */
export interface SignatureSlotRef {
  slotKey: string;
  rowId?: string;
}

/** 칸 탭(서명 요청) — host가 서명 패드에 띄울 표시명까지 함께 받는다. */
export interface SignatureSlotSignRequest extends SignatureSlotRef {
  signerName?: string;
}

/**
 * host 주입 서명 슬롯 컨텍스트 — 값(걷은 서명 맵)과 수집 상호작용을 한 채널로 묶는다.
 *
 * - `signatures`: 서버 렌더 맵(`SlotSignatureRenderMap` — slotKey→(rowId|''→뷰)). host가
 *   GET /reports/:id/slot-signatures 로 확보·갱신한다. unknown 수용(외부 주입 방어는
 *   `slotSignatureFor`가 담당).
 * - `onRequestSign`: 칸 탭 → host가 서명 패드 모달을 열고 PUT 후 `signatures`를 재조회한다.
 *   미주입 = 표시 전용(캔버스·external·생성 모드 — reportId가 없어 걷을 수 없다).
 * - `onRequestDelete`: 서명 지우기(S13 재수집) — DRAFT·REJECTED 작성 모드 한정으로 주입.
 */
export interface ComposerSignatureSlotsContext {
  signatures: unknown;
  onRequestSign?: (req: SignatureSlotSignRequest) => void;
  onRequestDelete?: (req: SignatureSlotRef) => void;
}

export interface SignatureSlotViewProps {
  /**
   * 서명 값 — 비어 있지 않은 문자열이면 이미지 src(data URL 또는 파일 URL)로 본다.
   * 그 외(undefined/null/객체/빈 문자열)는 전부 "아직 안 받음"이다. P2부터는 resolved 채널
   * (`resolveSlotSignatureDisplay`)이 이 값을 공급한다 — content 행 값은 항상 빈 값이다.
   */
  value?: unknown;
  /** 서명자 표시명 — 빈 칸 안내에 병기("홍길동 서명"). 미지정이면 이름 없이 "서명". */
  signerName?: string;
  /** 빈 칸 문구 override(저작자 지정 placeholder). 미지정=signerName 기반 기본 문구. */
  placeholder?: string;
  /** 칸 탭(서명 요청/재서명) — 미지정=표시 전용. */
  onRequestSign?: () => void;
  /** 서명 지우기(작성 모드 한정 어포던스) — 서명이 있을 때만 × 오버레이 노출. */
  onRequestDelete?: () => void;
}

/** 빈 칸 안내 문구 — 이름을 알면 누구 칸인지 밝힌다(릴레이 수집에서 기기를 넘길 때 필요하다). */
function emptyLabel(signerName?: string, placeholder?: string): string {
  if (placeholder) return placeholder;
  const name = signerName?.trim();
  return name ? `${name} 서명` : '서명';
}

/**
 * 행에서 서명자 표시명 읽기 — `signerNameField`가 가리키는 필드의 값(문자열만).
 *
 * 선언이 없거나 그 필드가 비어 있으면 undefined다: 서명 칸은 이름 없이도 성립해야 한다
 * (이름을 못 읽었다고 칸을 감추면 서명 자체를 못 받는다). 값이 문자열이 아니면(객체·배열) 이름으로
 * 쓸 수 없으므로 마찬가지로 undefined — String()으로 접으면 `[object Object]`가 서명란에 찍힌다.
 *
 * 행 그리드·formTable 반복 셀이 공유한다(surface별 재구현 금지 — 한쪽만 이름을 띄우면 같은 슬롯이
 * 화면마다 다른 사람 것처럼 보인다).
 */
export function rowSignerName(
  row: Record<string, unknown> | undefined,
  field: { signerNameField?: string },
): string | undefined {
  if (!row || !field.signerNameField) return undefined;
  const raw = row[field.signerNameField];
  if (typeof raw !== 'string') return undefined;
  const name = raw.trim();
  return name === '' ? undefined : name;
}

/**
 * 슬롯 표시값 해소 — 3채널(반복 위젯·formTable 반복 행 셀·본문 반복 셀)이 공유하는 단일 지점.
 *
 * 주소 파생: slotKey는 반드시 `resolveSignatureSlotKey()`(고정 slotKey 존중 + 파생)로,
 * rowId는 행 객체의 `rowId`(seedRowsWithIdentity/createBlankRow가 부여)로. 값은 host/liveData가
 * 실은 렌더 맵에서 `slotSignatureFor()`로 읽는다 — content 행 값은 읽지 않는다(§3-2).
 * rowId가 없는 행(비정상 조립 데이터)은 단독 슬롯('')과 주소가 겹치면 안 되므로 값 없음 처리한다.
 */
export function resolveSlotSignatureDisplay(
  signatures: unknown,
  dataSourceKey: string,
  field: Pick<DataSourceFieldMap, 'field' | 'slotKey' | 'signerNameField'>,
  row: Record<string, unknown> | undefined,
): { slotKey: string; rowId: string | undefined; view: SlotSignatureRenderView | undefined } {
  const slotKey = resolveSignatureSlotKey(dataSourceKey, field);
  const rawRowId = row?.rowId;
  const rowId = typeof rawRowId === 'string' && rawRowId !== '' ? rawRowId : undefined;
  // 행 스코프인데 rowId가 없으면 조회하지 않는다 — ''(단독 슬롯 키)로 접으면 남의 주소를 읽는다.
  const view = rowId === undefined ? undefined : slotSignatureFor(signatures, slotKey, rowId);
  return { slotKey, rowId, view };
}

/**
 * 3채널(반복 위젯·행 그리드·formTable 반복 셀) 공용 — 슬롯 주소 해소 + host 콜백 바인딩을
 * `SignatureSlotView` props 한 벌로 접는다(채널별 재구현 금지: 한 채널만 조건이 다르면
 * 같은 슬롯이 화면마다 다르게 동작한다).
 *
 * rowId 없는 행(정체성 부재 — 비정상 조립 데이터)은 상호작용을 붙이지 않는다: 주소가
 * 성립하지 않아 걷어도 저장할 곳이 없다(단독 슬롯('')로 오기록되면 남의 주소를 덮는다).
 */
export function signatureSlotViewProps(
  slots: ComposerSignatureSlotsContext | undefined,
  dataSourceKey: string,
  field: Pick<DataSourceFieldMap, 'field' | 'slotKey' | 'signerNameField'>,
  row: Record<string, unknown> | undefined,
): Pick<SignatureSlotViewProps, 'value' | 'signerName' | 'onRequestSign' | 'onRequestDelete'> {
  const { slotKey, rowId, view } = resolveSlotSignatureDisplay(
    slots?.signatures,
    dataSourceKey,
    field,
    row,
  );
  const signerName = rowSignerName(row, field);
  const onRequestSign = slots?.onRequestSign;
  const onRequestDelete = slots?.onRequestDelete;
  return {
    value: view?.signatureData,
    signerName,
    onRequestSign:
      onRequestSign && rowId !== undefined
        ? () => onRequestSign({ slotKey, rowId, signerName })
        : undefined,
    // 지우기는 서명이 실제로 있을 때만(빈 칸에 × 어포던스가 뜨면 오조작 유도).
    onRequestDelete:
      onRequestDelete && rowId !== undefined && view !== undefined
        ? () => onRequestDelete({ slotKey, rowId })
        : undefined,
  };
}

export function SignatureSlotView({
  value,
  signerName,
  placeholder,
  onRequestSign,
  onRequestDelete,
}: SignatureSlotViewProps) {
  const src = typeof value === 'string' ? value.trim() : '';
  const label = emptyLabel(signerName, placeholder);

  if (src === '') {
    if (!onRequestSign) {
      return <div style={EMPTY_SLOT_STYLE}>{label}</div>;
    }
    // 탭 → 서명 패드(host). 시각은 표시 전용 빈 칸과 동일(WYSIWYG) + 커서만 상호작용을 알린다.
    return (
      <button
        type="button"
        style={{ ...EMPTY_SLOT_STYLE, ...TAPPABLE_RESET_STYLE }}
        onClick={onRequestSign}
        aria-label={`${label} 받기`}
      >
        {label}
      </button>
    );
  }

  // 서명됨 상태의 접근성 이름은 **한 값**(`signedLabel`)이다 — 이미지 alt와 재서명·지우기 버튼이
  // 같은 것을 가리키므로. 빈 칸 안내(`placeholder`)는 빈 상태의 문구라 여기엔 섞지 않는다(종전엔 alt만
  // placeholder를 안 타서 aria-label과 어긋났다 — 2026-08-30 리뷰 INFO).
  const signedLabel = emptyLabel(signerName);
  const img = (
    <div style={SIGNED_SLOT_STYLE}>
      <img src={src} alt={signedLabel} style={SIGNATURE_IMG_STYLE} />
    </div>
  );

  if (!onRequestSign && !onRequestDelete) return img;

  // 서명됨 + 작성 모드: 탭=재서명(교체 upsert — S13), × 오버레이=삭제. 오버레이는 absolute라
  // 흐름 폭·높이를 바꾸지 않는다(레이아웃 중립 규약).
  const handleDelete = (e: MouseEvent<HTMLButtonElement>): void => {
    e.stopPropagation(); // 재서명 탭과 분리 — 지우기가 서명 패드까지 열면 오조작이 된다.
    onRequestDelete?.();
  };
  const body = onRequestSign ? (
    <button
      type="button"
      style={{ ...TAPPABLE_RESET_STYLE, display: 'block', width: '100%', border: 'none', background: 'transparent' }}
      onClick={onRequestSign}
      aria-label={`${signedLabel} 다시 받기`}
    >
      {img}
    </button>
  ) : (
    img
  );
  return (
    <span style={OVERLAY_ANCHOR_STYLE}>
      {body}
      {onRequestDelete && (
        <button
          type="button"
          style={DELETE_BADGE_STYLE}
          onClick={handleDelete}
          aria-label={`${signedLabel} 지우기`}
        >
          {String.fromCharCode(215) /* × — 제어문자/리터럴 특수문자 금지 수칙에 따라 코드로 생성 */}
        </button>
      )}
    </span>
  );
}

export default SignatureSlotView;
